/**
 * ZA Player Pro — Get Native App Banner (v1.4.77)
 *
 * Fallback banner shown inside the web-player's error overlay when a
 * stream fails to decode in the browser (H.265, AC-3, MPEG-2, etc.)
 * because browsers don't support every codec that VLC / ExoPlayer do.
 *
 * Detects the user's platform via UA and links to the correct download
 * for that device — Android APK, iOS App Store, Windows .exe, Samsung
 * Store, LG Store, etc.
 *
 * Only rendered on the web/PWA build. Auto-hides on Capacitor / Electron
 * / native shells where the user already has the full-codec app.
 */

import React from "react";
import { Download, Cpu } from "lucide-react";

// -----------------------------------------------------------------------------
// Platform detection
// -----------------------------------------------------------------------------
function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const p = navigator.platform || "";

  // TVs first — they often look like Android but need store links
  if (/Tizen/i.test(ua)) return "samsung-tv";
  if (/webOS|Web0S/i.test(ua)) return "lg-tv";
  if (/VIDAA/i.test(ua)) return "hisense-tv";
  if (/AFT[A-Z]|AFTS|AFTM|AFTB|Silk|KFAPWI|KFTBWI/i.test(ua)) return "fire-tv";
  if (/GoogleTV|BRAVIA|ADT-|ANDROID.TV|Nexus.Player|SHIELD Android TV/i.test(ua)) return "android-tv";

  // Phones / tablets
  if (/iPad|iPhone|iPod/i.test(ua) && !window.MSStream) return "ios";
  if (/Android/i.test(ua)) return "android";

  // Desktops
  if (/Windows/i.test(ua) || /Win/i.test(p)) return "windows";
  if (/Macintosh|Mac OS X|MacIntel/i.test(ua)) return "mac";
  if (/Linux/i.test(ua)) return "linux";

  return "unknown";
}

function isNativeAlready() {
  if (typeof window === "undefined") return false;
  if (window.Capacitor?.isNativePlatform?.()) return true;
  if (window.__ZAPP_IS_ELECTRON__) return true;
  return false;
}

// -----------------------------------------------------------------------------
// Per-platform button config
// -----------------------------------------------------------------------------
const PLATFORM_CONFIG = {
  ios: {
    label: "Download for iPhone / iPad",
    url: "https://apps.apple.com/app/za-player-pro/",
    reason: "iOS Safari can't decode some Dolby audio & H.265 streams. The App Store build handles every codec.",
  },
  android: {
    label: "Download Android APK",
    url: "https://zaplayerpro.club/download",
    reason: "The Android app uses ExoPlayer + libVLC for full codec support (Dolby, H.265, MPEG-2).",
  },
  "android-tv": {
    label: "Get on Android TV",
    url: "https://zaplayerpro.club/download",
    reason: "Sideload the APK on your TV for full codec support.",
  },
  "fire-tv": {
    label: "Get on Fire TV",
    url: "https://zaplayerpro.club/download",
    reason: "Sideload via Downloader app for full codec support on Fire TV.",
  },
  "samsung-tv": {
    label: "Get on Samsung TV Store",
    url: "https://www.samsung.com/za/apps/za-player-pro/",
    reason: "The Samsung TV native app handles every stream format your provider ships.",
  },
  "lg-tv": {
    label: "Get on LG Content Store",
    url: "https://www.lg.com/za/lgapp/lg-content-store/",
    reason: "The LG TV native app handles every stream format your provider ships.",
  },
  "hisense-tv": {
    label: "Get on Hisense VIDAA Store",
    url: "https://www.hisense.com/vidaa/",
    reason: "The Hisense VIDAA native app handles every stream format your provider ships.",
  },
  windows: {
    label: "Download for Windows",
    url: "https://zaplayerpro.club/download",
    reason: "The Windows app uses mpv for playback and handles every codec + subtitle format.",
  },
  mac: {
    label: "Download for macOS",
    url: "https://apps.apple.com/app/za-player-pro/",
    reason: "The App Store build handles every codec (Dolby, H.265, MPEG-2).",
  },
  linux: {
    label: "Download for Linux",
    url: "https://zaplayerpro.club/download",
    reason: "The Linux app uses mpv for playback and handles every codec.",
  },
  unknown: {
    label: "Get the full app",
    url: "https://zaplayerpro.club/get",
    reason: "Some streams need the native app for Dolby audio, H.265, or MPEG-2 support that browsers don't provide.",
  },
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
export default function GetNativeAppBanner({ compact = false }) {
  if (isNativeAlready()) return null;

  const platform = detectPlatform();
  const cfg = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.unknown;

  return (
    <div
      data-testid="get-native-app-banner"
      data-platform={platform}
      className={
        compact
          ? "mt-3 flex items-center gap-3 rounded-xl bg-neutral-900/80 border border-white/10 p-3 text-left max-w-md"
          : "mt-5 flex flex-col items-start gap-3 rounded-2xl bg-gradient-to-br from-fuchsia-900/40 to-purple-900/40 border border-fuchsia-500/30 p-4 text-left max-w-md"
      }
    >
      <div className="flex items-start gap-3">
        <Cpu className="w-5 h-5 text-fuchsia-300 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold">
            This stream needs the full app
          </p>
          <p className="text-white/60 text-xs mt-1">{cfg.reason}</p>
        </div>
      </div>
      <a
        data-testid="get-native-app-btn"
        href={cfg.url}
        target={cfg.url.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 self-stretch justify-center rounded-lg bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-600 hover:to-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors"
      >
        <Download className="w-4 h-4" />
        {cfg.label}
      </a>
    </div>
  );
}
